import React, { MouseEventHandler } from 'react';
import useResponsiveness from '_helpers/hooks/useResponsiveness';
import Section from 'components/organisms/section';
import ExternalLink from '../external-link';
import Badge from '../badge';
import MoreInformationIcon from '../more-info-icon';

type TimeLineEvent = {
  date: string;
  hideLine?: boolean;
  dashedLine?: boolean;
  filledLine?: boolean;
  title: string;
  company: string;
  companyUrl: string;
  badges?: string[];
  onMoreInfoClick?: MouseEventHandler<HTMLButtonElement>;
};

type TimelineProps = {
  events: TimeLineEvent[];
};

export default function Timeline({ events }: TimelineProps) {
  const { isMobile, isTablet, isDesktop } = useResponsiveness();

  return (
    <Section styles="h-[80%]">
      <div className="h-full flex flex-col justify-between py-4">
        {events?.map((event, index) => (
          <div key={event?.date} className="flex-1 flex gap-4 items-start">
            {isDesktop() && (
              <div className={`w-[150px] text-xs lg:text-base whitespace-nowrap ${index === 0 ? 'font-bold' : ''}`}>
                {event?.date}
              </div>
            )}
            <div className="relative flex items-start gap-3 lg:gap-6 h-full">
              <div className="flex flex-col items-center">
                {index > 0 && !event?.hideLine && (
                  <div
                    className={`absolute bottom-[calc(100%-0.3rem)] h-[calc(100%-0.6rem)] lg:h-[calc(100%-1.2rem)] border-l-2 lg:border-l-4 opacity-30 ${
                      event?.dashedLine ? 'border-dashed' : ''
                    } ${event?.filledLine ? 'opacity-100' : ''}`}
                  />
                )}
                <div
                  className={`bg-white rounded-full h-2 w-2 lg:h-4 lg:w-4 mt-[6px] ${index !== 0 ? 'opacity-30' : ''}`}
                />
              </div>
              <div className="flex flex-col lg:flex-row items-center gap-1 lg:gap-4">
                <div className="block lg:flex items-center gap-1">
                  {!isDesktop() && (
                    <div
                      className={`w-[200px] text-xs lg:text-base whitespace-nowrap ${index === 0 ? 'font-bold' : ''}`}>
                      {event?.date}
                    </div>
                  )}
                  <div className="text-sm lg:text-2xl font-semibold whitespace-nowrap">
                    {event?.title} {isDesktop() && `at ${event?.company}`}
                  </div>
                  {isMobile() || isTablet() ? (
                    <div className="flex items-center gap-1">
                      <div className="text-xs lg:text-lg">{event?.company}</div>
                      <ExternalLink href={event?.companyUrl} />
                      {event?.onMoreInfoClick && <MoreInformationIcon onClick={event?.onMoreInfoClick} />}
                    </div>
                  ) : (
                    <>
                      <ExternalLink href={event?.companyUrl} />
                      {event?.onMoreInfoClick && <MoreInformationIcon onClick={event?.onMoreInfoClick} />}
                    </>
                  )}
                </div>
                <div className="w-full flex justify-start gap-1 lg:gap-2">
                  {event?.badges?.map(badgeText => (
                    <Badge key={badgeText} text={badgeText} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}
